import React from "react";
import { motion } from "framer-motion";
import { useContent, resolveAsset } from "@/context/ContentContext";
import RichText from "@/components/RichText";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { content } = useContent() || {};
  const h = content?.hero || {};

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen vignette overflow-hidden pt-20"
    >
      <div className="absolute inset-0">
        <img
          src={resolveAsset(h.image || "/assets/p-cyber.jpg")}
          alt="Ranjana Baruah — cinematic portrait"
          className="w-full h-full object-cover object-[70%_center] md:object-[65%_center] opacity-65"
          style={{ filter: "grayscale(35%) contrast(1.05) brightness(0.85)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/75 to-[#050505]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
      </div>

      <div className="absolute left-6 md:left-12 top-1/2 -translate-y-1/2 hidden md:block">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1, ease }}
          className="font-mono text-[10px] tracking-[0.4em] text-[#a3a3a3] uppercase"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          {h.reel_label || "Reel · 01 — Long Lasting Memory"}
        </motion.div>
      </div>

      <div className="absolute right-6 md:right-12 top-32 hidden md:flex flex-col items-end gap-2">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, ease }}
          className="overline"
        >
          {h.portfolio_year || "Portfolio · MMXXVI"}
        </motion.span>
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8, ease }}
          className="font-mono text-[10px] tracking-[0.3em] text-[#c01515]"
        >
          ● ON RECORD
        </motion.span>
      </div>

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 pt-28 md:pt-44 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease }}
          className="overline mb-8"
        >
          <span className="accent-bar" />
          <RichText value={h.kicker || "The portfolio of a thriller mind"} />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.2, ease }}
          className="font-display font-light text-[#f5f5f5] leading-[0.85] tracking-[-0.04em]"
          style={{ fontSize: "clamp(3.5rem, 13vw, 13rem)" }}
        >
          {h.name_first || "Ranjana"}
          <br />
          <span className="italic font-light text-[#c01515]">
            {h.name_last || "Baruah"}
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7, ease }}
          className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
        >
          <div className="md:col-span-5 md:col-start-1">
            <p className="font-display italic text-2xl md:text-3xl text-[#f5f5f5]/85 leading-tight">
              {h.tagline_a || "Engineer. Artist."}
              <br />
              {h.tagline_b || "Designer. Founder."}
            </p>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="overline mb-3">{h.now_label || "Now Crafting"}</p>
            <RichText
              as="p"
              className="font-display text-xl text-[#f5f5f5]/90 leading-snug"
              value={h.now_body ||
                "Long-lasting memory through suspense, mystery and the quiet edges of the human nature."}
            />
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 left-0 right-0 px-6 md:px-12 max-w-[1500px] mx-auto flex items-center justify-between"
      >
        <span className="overline">{h.scroll_hint || "Scroll · For Dossier"}</span>
        <span className="overline hidden md:inline">{h.coords || "N — 26.1445° · E — 91.7362°"}</span>
      </motion.div>
    </section>
  );
}
